import { Head, Link, router } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import Pagination from "@/components/ui/pagination";
import { PagyMeta } from "@/types/pagy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { STATUS_OPTIONS } from "./constants";

interface Agency {
  id: number;
  name: string;
}

interface Region {
  id: number;
  name: string;
}

interface Property {
  id: number;
  title: string;
  kind?: string | null;
  status?: string | null;
  price?: number | null;
  agency?: Agency | null;
  region?: Region | null;
  created_at: string;
}

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map(({ value, label }) => [value, label]));

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-slate-100 text-slate-800 border-slate-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  archived: "bg-muted/20 border-muted text-muted-foreground",
};

interface Props {
  properties: Property[];
  pagy: PagyMeta;
}

export default function Index({ properties, pagy }: Props) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("ar-UK", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  };

  const formatPrice = (price: number | null | undefined) => {
    if (!price) return "—";
    return new Intl.NumberFormat("en-SA", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleDelete = (id: number) => {
    router.delete(`/desk/properties/${id}`);
  };

  return (
    <>
      <Head title="العقارات" />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>العقارات</CardTitle>
              <CardDescription>إدارة قائمة العقارات وعرض تفاصيلها</CardDescription>
            </div>
            <Button asChild>
              <Link href="/desk/properties/new">
                <Plus className="ms-2 h-4 w-4" />
                إضافة عقار جديد
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {properties.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              لا توجد عقارات مسجلة حالياً
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>العنوان</TableHead>
                  <TableHead>النوع</TableHead>
                  <TableHead>المنطقة</TableHead>
                  <TableHead>السعر</TableHead>
                  <TableHead>الوكالة</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>تاريخ الإضافة</TableHead>
                  <TableHead className="w-[160px]">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {properties.map((property) => (
                  <TableRow key={property.id}>
                    <TableCell>{property.title}</TableCell>
                    <TableCell>{property.kind || "—"}</TableCell>
                    <TableCell>{property.region?.name || "—"}</TableCell>
                    <TableCell>{formatPrice(property.price)}</TableCell>
                    <TableCell>{property.agency?.name || "—"}</TableCell>
                    <TableCell>
                      {property.status ? (
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                            STATUS_STYLES[property.status] ||
                            "bg-muted/20 border-muted text-muted-foreground"
                          }`}
                        >
                          {STATUS_LABELS[property.status] || property.status}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{formatDate(property.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" asChild>
                          <Link href={`/desk/properties/${property.id}`}>
                            <Eye className="h-4 w-4" />
                            <span className="sr-only">عرض</span>
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" asChild>
                          <Link href={`/desk/properties/${property.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">تعديل</span>
                          </Link>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive">
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">حذف</span>
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
                              <AlertDialogDescription>
                                هل أنت متأكد من حذف هذا العقار؟
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleDelete(property.id)}
                              >
                                حذف
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Pagination pagy={pagy} basePath="/desk/properties" />
    </>
  );
}
